/**
 * Case-insensitive duplicate-name detection for the "name a new thing" dialogs.
 *
 * genesis enforces NO name uniqueness on any of these: there is no unique constraint on
 * document_group.name, document.document_name, person_group, pin_group or curriculum_vitae
 * (V0002__Tables.sql). So this check is not a latency optimisation over a server-side guarantee -
 * it is the only thing standing between the user and two rows they cannot tell apart.
 *
 * That matters for how callers treat it: a cap or a paging limit on the sibling list is a real hole,
 * not a graceful degradation, because nothing downstream will catch what this misses.
 *
 * Trimming and lower-casing both sides is the whole rule: "Mentoren", "mentoren" and " Mentoren "
 * are the same name to a person reading a list, whatever the database's collation thinks.
 */
export function hasDuplicateName(
  existing: Iterable<string>,
  candidate: string,
  /** Skip this name so renaming a row to its own current value is never "duplicate". */
  except?: string | null,
): boolean {
  const needle = normalizeName(candidate);
  if (!needle) return false;
  const self = except == null ? null : normalizeName(except);
  for (const name of existing) {
    const other = normalizeName(name);
    if (other && other === needle && other !== self) return true;
  }
  return false;
}

/** The comparison form of a name. Exported so callers can build their own maps consistently. */
export function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}
